import { z } from "zod"
import { NegligibleLaborThresholdPercent, PosType } from "../pos"
import {
  BikeBrand,
  ElectricSystem,
  ExtendedJob,
  LeasingCompany,
  ServiceAction,
  ServiceAvailability,
  ServiceBikeType,
  ServicePackage,
} from "../service"
import { CustomStoreInfo } from "./custom-store-info"
import { CustomerClub } from "./customer-club"
import { FeatureFlags } from "./feature-flags"
import { LegalDocument } from "./legal-document"
import { AllOpeningHours } from "./opening-hours"

export const NUM_MONTHS_UNTIL_CUSTOMER_IS_ANONYMISED = 18

export const StoreSchema = z.object({
  id: z.guid(),
  name: z.string(),
  slug: z.string(),
  alphanumericSenderId: z.string(),
  phoneNumber: z.string(),
  email: z.string().email(),
  coverPhoto: z.string().url(),
  address: z.string().nullable(),
  zipcode: z.string().nullable(),
  city: z.string().nullable(),
  coordinateLat: z.number().nullable(),
  coordinateLon: z.number().nullable(),
  googleMapsLink: z.string(),
  googlePlaceId: z.string().nullable(),
  googleAnalyticsMeasurementId: z.string().nullable(),
  iframeButtonColor: z.string().nullable(),
  type: z.string(),
  tier: z.string().nullable(),
  // Swedish organisationsnummer — required before the store's bikes can be
  // bought on julo.se (it identifies the store as seller on self-billing
  // invoices).
  organizationNumber: z.string().nullable(),
})

export const CreateStoreSchema = StoreSchema.omit({ id: true }).extend({
  address: z.string().optional(),
  zipcode: z.string().optional(),
  city: z.string().optional(),
  coordinateLat: z.number().optional(),
  coordinateLon: z.number().optional(),
  googlePlaceId: z.string().optional(),
  googleAnalyticsMeasurementId: z.string().optional(),
  iframeButtonColor: z.string().optional(),
  tier: z.string().optional(),
  organizationNumber: z.string().optional(),
})
export const CreateStoreWithoutPhotoSchema = CreateStoreSchema.omit({
  coverPhoto: true,
})
export const UpdateStoreSchema = CreateStoreSchema.partial()

export type Store = z.infer<typeof StoreSchema>

export type CreateStoreWithoutPhoto = z.infer<
  typeof CreateStoreWithoutPhotoSchema
>
export type UpdateStore = z.infer<typeof UpdateStoreSchema>

export interface ExtendedStore extends Store {
  featureFlags: FeatureFlags
  openingHours: AllOpeningHours | null
  privacyPolicy: LegalDocument | null
  termsOfService: LegalDocument | null
  customerClub: CustomerClub | null
  bookablePackages: ServicePackage[]
  partnerBrands: BikeBrand[]
  supportedElectricSystems: ElectricSystem[]
  supportedLeasingCompanies: LeasingCompany[]
  supportedBikeTypes: ServiceBikeType[]
  averageServiceTime: number
  allowsEmergencyService: boolean
  customBikeTypesEnabled: boolean
  hiddenServiceActions: ServiceAction[]
  distanceToStore: number | null
  availability: ServiceAvailability
  customStoreInfo: CustomStoreInfo[]
  googleRating: number | null
  googleRatingCount: number | null
  posSettings?: {
    type: PosType | null
    orderSaveMode: string
    miscellaneousMaterialsArticleId: string | null
    markAsDeliveredOnOrderCreation: boolean
    negligibleLaborThresholdPercent: NegligibleLaborThresholdPercent
    autoFinishOnPayment: boolean
    nutidPosSettings: { mode: string } | null
  } | null
  /** Fortnox is enabled for the store, connected or not. */
  fortnoxEnabled: boolean
  shopifySettings?: {
    shopifyStoreId: string
  } | null
  canPublishBikeForSale: boolean
}

export interface UpdateStoreLegacy {
  name?: string | null
  slug?: string | null
  alphanumericSenderId?: string | null
  phoneNumber?: string | null
  email?: string | null
  address?: string | null
  zipcode?: string | null
  city?: string | null
  coordinateLat?: number | null
  coordinateLon?: number | null
  googleMapsLink?: string | null
  googlePlaceId?: string | null
  googleAnalyticsMeasurementId?: string | null
  iframeButtonColor?: string | null
  tier?: string | null
  organizationNumber?: string | null
  featureFlags?: Partial<FeatureFlags> | null
}

export interface AdminStoreListItem {
  id: string
  name: string
  type: string
  tier: string | null
  city: string
  slug: string
  email: string
  hiddenOnJulo: boolean
  stripePayoutsEnabled: boolean
  whiteLabelBookingEnabled: boolean
  companiesEnabled: boolean
  customerClubEnabled: boolean
  testMode: boolean
}

export interface JobsListResult {
  totalPages: number
  jobs: ExtendedJob[]
}
